/**
 * DOM Builder - Direct DOM construction for performance.
 *
 * Using direct DOM APIs instead of innerHTML is significantly faster:
 * - No HTML parsing by the browser
 * - No template string concatenation
 * - Immediate element creation without serialization/deserialization
 *
 * Benchmark: DOM construction is ~2-3x faster than innerHTML for complex structures.
 */

import { GridClasses } from '../constants';
import { sanitizeHTML } from './sanitize';

// #region Element Factories
/**
 * Apply attributes to an element, skipping null/undefined values.
 * Shared by createElement/div/button to keep the bundle small.
 */
function applyAttrs(el: Element, attrs: Record<string, string | undefined> | undefined): void {
  if (!attrs) return;
  for (const key in attrs) {
    const value = attrs[key];
    if (value != null) el.setAttribute(key, value);
  }
}

/**
 * Create an element with attributes and optional children.
 * Optimized helper that avoids repeated function calls.
 */
function createElement<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs?: Record<string, string>,
  children?: (Node | string | null | undefined)[],
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  applyAttrs(el, attrs);

  if (children) {
    for (const child of children) {
      if (child == null) continue;
      if (typeof child === 'string') {
        el.appendChild(document.createTextNode(child));
      } else {
        el.appendChild(child);
      }
    }
  }

  return el;
}

/**
 * Create an element with class (common pattern).
 */
function div(className?: string, attrs?: Record<string, string>): HTMLDivElement {
  const el = document.createElement('div');
  if (className) el.className = className;
  applyAttrs(el, attrs);
  return el;
}

/**
 * Create a button element. Always sets `type="button"` (callers can override
 * via `attrs`) so internal grid buttons inside a <form> never default to
 * type="submit". See issue #296.
 */
function button(className?: string, attrs?: Record<string, string>, content?: string | Node): HTMLButtonElement {
  const el = document.createElement('button');
  el.type = 'button';
  if (className) el.className = className;
  applyAttrs(el, attrs);
  if (content) {
    if (typeof content === 'string') {
      el.textContent = content;
    } else {
      el.appendChild(content);
    }
  }
  return el;
}
// #endregion

// #region Grid Templates
/**
 * Template for grid content (the core scrollable grid area).
 * Pre-built once, then cloned for each grid instance.
 */
const gridContentTemplate = document.createElement('template');
gridContentTemplate.innerHTML = `
  <div class="tbw-scroll-area">
    <div class="rows-body-wrapper">
      <div class="rows-body" role="grid">
        <div class="header" role="rowgroup">
          <div class="header-row" role="row" part="header-row"></div>
        </div>
        <div class="rows-container" role="presentation">
          <div class="rows-viewport" role="presentation">
            <div class="rows"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div class="faux-vscroll">
    <div class="faux-vscroll-spacer"></div>
  </div>
  <div class="tbw-sr-only" aria-live="polite" aria-atomic="true"></div>
`;

/**
 * Clone the grid content structure.
 * Using template cloning is faster than createElement for nested structures.
 */
export function cloneGridContent(): DocumentFragment {
  return gridContentTemplate.content.cloneNode(true) as DocumentFragment;
}
// #endregion

// #region Grid DOM Building
/**
 * Build the grid root structure using direct DOM construction.
 * This is called once per grid instance during initial render.
 */
export interface GridDOMOptions {
  hasShell: boolean;
  /** Shell header element (pre-built) */
  shellHeader?: Element;
  /** Shell body element with tool panel (pre-built) */
  shellBody?: Element;
}

/**
 * Build the complete grid DOM structure.
 * Returns a DocumentFragment ready to be appended to shadow root.
 */
export function buildGridDOM(options: GridDOMOptions): DocumentFragment {
  const fragment = document.createDocumentFragment();

  const root = div(options.hasShell ? `${GridClasses.ROOT} has-shell` : GridClasses.ROOT);

  if (options.hasShell && options.shellBody) {
    // Shell mode: optional header + body (with grid content inside).
    // The header bar may be suppressed via `shell.header.visible: false`, in
    // which case only the body (and its tool panel) is rendered.
    if (options.shellHeader) root.appendChild(options.shellHeader);
    root.appendChild(options.shellBody);
  } else {
    // No shell: just grid content in wrapper
    const contentWrapper = div('tbw-grid-content');
    contentWrapper.appendChild(cloneGridContent());
    root.appendChild(contentWrapper);
  }

  fragment.appendChild(root);
  return fragment;
}

/**
 * Build the BARE grid DOM (no shell chrome) directly into `renderRoot`.
 *
 * Preserves all existing direct child nodes EXCEPT the grid's own
 * `.tbw-grid-root` (which is rebuilt), so user/framework light-DOM stays intact
 * in its original order — including non-element nodes (comment anchors used by
 * Angular `@if`/`@for` and other structural directives as positional markers).
 *
 * Core's render path always builds the bare grid; the shell, when configured,
 * is wrapped around this freshly built grid by the auto-registered ShellPlugin
 * in the `afterStructuralRender` hook (extraction #370 — DOM ownership
 * inversion). Core therefore holds no shell-construction knowledge.
 */
export function buildBareGridDOMIntoElement(renderRoot: Element): void {
  const preservedNodes: Node[] = [];
  for (let n: Node | null = renderRoot.firstChild; n; n = n.nextSibling) {
    if (n.nodeType === 1 && (n as Element).classList.contains(GridClasses.ROOT)) continue;
    preservedNodes.push(n);
  }

  // Clear existing content (this would delete light DOM nodes, so we preserved them first).
  renderRoot.replaceChildren();

  // Re-append preserved nodes in original order (hidden elements are used for config).
  // IMPORTANT: These are prepended before .tbw-grid-root, so `renderRoot.children[0]`
  // is NOT the grid root. Use `querySelector('.tbw-grid-root')` instead.
  for (const n of preservedNodes) {
    renderRoot.appendChild(n);
  }

  renderRoot.appendChild(buildGridDOM({ hasShell: false }));
}
// #endregion

// #region Shell Header
/**
 * Build shell header using direct DOM construction.
 *
 * Note: The grid no longer creates buttons from config (icon/action).
 * Config/API buttons only use element or render function.
 * Light DOM buttons are slotted directly.
 * The ONLY button the grid creates is the panel toggle.
 */
export interface ShellHeaderOptions {
  title?: string;
  hasPanels: boolean;
  isPanelOpen: boolean;
  toolPanelIcon?: string;
  /**
   * Whether to render the built-in tool panel toggle button and the
   * adjacent separator. Independent of `hasPanels` so panels can remain
   * registered (and openable via the public API) while the consumer
   * supplies their own toggle button.
   * @default true when hasPanels is true
   */
  showToggle?: boolean;
  /** Config toolbar contents with render function (pre-sorted by order) */
  configButtons: Array<{
    id: string;
    hasRender?: boolean;
  }>;
  /** API toolbar contents with render function (pre-sorted by order) */
  apiButtons: Array<{
    id: string;
    hasRender?: boolean;
  }>;
}

/**
 * Build shell header element directly without innerHTML.
 */
export function buildShellHeader(options: ShellHeaderOptions): HTMLDivElement {
  const header = div('tbw-shell-header', { part: 'shell-header', role: 'presentation' });

  // Title
  if (options.title) {
    const titleEl = div('tbw-shell-title');
    titleEl.textContent = options.title;
    header.appendChild(titleEl);
  }

  // Shell content with placeholder for light DOM header content
  const content = div('tbw-shell-content', {
    part: 'shell-content',
    role: 'presentation',
    'data-light-dom-header-content': '',
  });
  header.appendChild(content);

  // Toolbar
  const toolbar = div('tbw-shell-toolbar', { part: 'shell-toolbar', role: 'presentation' });

  // Placeholders for config toolbar contents with render function
  for (const btn of options.configButtons) {
    if (btn.hasRender) {
      toolbar.appendChild(div('tbw-toolbar-content-slot', { 'data-toolbar-content': btn.id }));
    }
  }
  // Placeholders for API toolbar contents with render function
  for (const btn of options.apiButtons) {
    if (btn.hasRender) {
      toolbar.appendChild(div('tbw-toolbar-content-slot', { 'data-toolbar-content': btn.id }));
    }
  }

  // Separator between custom content and panel toggle
  const hasCustomContent =
    options.configButtons.some((b) => b.hasRender) || options.apiButtons.some((b) => b.hasRender);
  // Default showToggle to hasPanels for backwards compat; suppress both the
  // button and the separator when the consumer opts out.
  const showToggle = options.hasPanels && options.showToggle !== false;
  if (hasCustomContent && showToggle) {
    toolbar.appendChild(div('tbw-toolbar-separator'));
  }

  // Panel toggle button (suppressed when shell.header.toolPanelToggle is false)
  if (showToggle) {
    const toggleBtn = button(options.isPanelOpen ? 'tbw-toolbar-btn active' : 'tbw-toolbar-btn', {
      'data-panel-toggle': '',
      title: 'Settings',
      'aria-label': 'Toggle settings panel',
      'aria-pressed': String(options.isPanelOpen),
      'aria-controls': 'tbw-tool-panel',
    });
    toggleBtn.dataset.icon = 'tool-panel';
    if (options.toolPanelIcon !== undefined) {
      toggleBtn.innerHTML = sanitizeHTML(options.toolPanelIcon);
    }
    toolbar.appendChild(toggleBtn);
  }

  header.appendChild(toolbar);
  return header;
}
// #endregion

// #region Shell Body
/**
 * Build shell body (grid content + optional tool panel).
 */
export interface ShellBodyOptions {
  position: 'left' | 'right';
  /**
   * Layout mode for the tool panel.
   * - `'overlay'` (default): panel is absolutely positioned over the grid.
   * - `'push'`: panel participates in flex layout, shrinking the grid sibling.
   * - `'dropdown'`: panel is a native popover anchored near a trigger element.
   */
  mode?: 'overlay' | 'push' | 'dropdown';
  isPanelOpen: boolean;
  expandIcon?: string;
  collapseIcon?: string;
  /**
   * Render an in-panel close (✕) button in the panel's top-right corner.
   * Used when the shell header bar is hidden (`shell.header.visible: false`)
   * so the panel can be dismissed without the built-in header toggle.
   */
  showCloseButton?: boolean;
  /** Sorted panels for accordion */
  panels: Array<{
    id: string;
    title: string;
    icon?: string;
    isExpanded: boolean;
  }>;
}

/**
 * Build shell body element directly without innerHTML.
 */
export function buildShellBody(options: ShellBodyOptions): HTMLDivElement {
  const mode = options.mode ?? 'overlay';
  const body = div('tbw-shell-body', { 'data-mode': mode });
  const hasPanel = options.panels.length > 0;
  const isSinglePanel = options.panels.length === 1;

  // Grid content wrapper with cloned grid structure
  const gridContent = div('tbw-grid-content');
  gridContent.appendChild(cloneGridContent());

  // Tool panel
  let panelEl: HTMLElement | null = null;
  if (hasPanel) {
    panelEl = createElement('aside', {
      class: options.isPanelOpen ? 'tbw-tool-panel open' : 'tbw-tool-panel',
      part: 'tool-panel',
      'data-position': options.position,
      role: 'presentation',
      id: 'tbw-tool-panel',
      // In dropdown mode the panel is a manually-controlled native popover
      // (shown/hidden via showPopover()/hidePopover() from the controller).
      ...(mode === 'dropdown' ? { popover: 'manual' } : {}),
    });

    // Resize handle
    const resizeHandlePosition = options.position === 'left' ? 'right' : 'left';
    panelEl.appendChild(
      div('tbw-tool-panel-resize', {
        'data-resize-handle': '',
        'data-handle-position': resizeHandlePosition,
        'aria-hidden': 'true',
      }),
    );

    // Optional close (✕) button — rendered (overlay/push only) when the shell
    // header bar is hidden, so the panel can always be dismissed. Dropdown mode
    // never reaches here (showCloseButton is gated off upstream). `data-panel-close`
    // is wired by setupShellEventListeners to close the panel.
    //
    // Placement:
    // - Multiple panels: a dedicated slim header row above the accordion.
    // - Single panel: inlined onto the (only) accordion header row so it shares
    //   the line with the panel title instead of wasting a full-width row.
    const makeCloseButton = (inline: boolean): HTMLButtonElement => {
      const closeBtn = button(inline ? 'tbw-tool-panel-close tbw-tool-panel-close--inline' : 'tbw-tool-panel-close', {
        part: 'tool-panel-close',
        'data-panel-close': '',
        title: 'Close panel',
        'aria-label': 'Close panel',
        'aria-controls': 'tbw-tool-panel',
      });
      closeBtn.textContent = '✕';
      return closeBtn;
    };
    const inlineClose = options.showCloseButton === true && isSinglePanel;
    if (options.showCloseButton && !isSinglePanel) {
      const panelHeader = div('tbw-tool-panel-header', { part: 'tool-panel-header' });
      panelHeader.appendChild(makeCloseButton(false));
      panelEl.appendChild(panelHeader);
    }

    // Panel content with accordion
    const panelContent = div('tbw-tool-panel-content', { role: 'presentation' });
    const accordion = div('tbw-accordion');

    for (const panel of options.panels) {
      const sectionClasses = `tbw-accordion-section${panel.isExpanded ? ' expanded' : ''}${isSinglePanel ? ' single' : ''}${inlineClose ? ' has-inline-close' : ''}`;
      const section = div(sectionClasses, { 'data-section': panel.id });

      // Accordion header button
      const headerBtn = button('tbw-accordion-header', {
        'aria-expanded': String(panel.isExpanded),
        'aria-controls': `tbw-section-${panel.id}`,
      });
      if (isSinglePanel) headerBtn.setAttribute('aria-disabled', 'true');

      // Icon
      if (panel.icon) {
        const iconSpan = createElement('span', { class: 'tbw-accordion-icon' });
        iconSpan.innerHTML = sanitizeHTML(panel.icon);
        headerBtn.appendChild(iconSpan);
      }

      // Title
      const titleSpan = createElement('span', { class: 'tbw-accordion-title' });
      titleSpan.textContent = panel.title;
      headerBtn.appendChild(titleSpan);

      // Chevron (hidden for single panel) — always use expandIcon, CSS rotation handles state
      if (!isSinglePanel) {
        const chevronSpan = createElement('span', { class: 'tbw-accordion-chevron', 'data-icon': 'expand' });
        if (options.expandIcon !== undefined) {
          chevronSpan.innerHTML = sanitizeHTML(options.expandIcon);
        }
        headerBtn.appendChild(chevronSpan);
      }

      // Single-panel close button shares the header row with the panel title.
      if (inlineClose) {
        const headerRow = div('tbw-accordion-header-row');
        headerRow.appendChild(headerBtn);
        headerRow.appendChild(makeCloseButton(true));
        section.appendChild(headerRow);
      } else {
        section.appendChild(headerBtn);
      }

      // Accordion content (empty, will be filled by panel render functions)
      section.appendChild(
        div('tbw-accordion-content', {
          id: `tbw-section-${panel.id}`,
          role: 'presentation',
        }),
      );

      accordion.appendChild(section);
    }

    panelContent.appendChild(accordion);
    panelEl.appendChild(panelContent);
  }

  // Append in correct order based on position
  if (options.position === 'left' && panelEl) {
    body.appendChild(panelEl);
    body.appendChild(gridContent);
  } else {
    body.appendChild(gridContent);
    if (panelEl) body.appendChild(panelEl);
  }

  return body;
}
// #endregion
